import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { getPlaceholderData, resolvePlaceholder } from 'modules/manager/domain/placeholder';
import type { PlaceholderFacade, ResolvedValue, ResolverContext } from 'modules/manager/domain/placeholder';
import { TemplateType, isPlaceholderKeyValidFor } from 'modules/manager/domain/template-type';

export interface SubstitutePlaceholdersOptions {
  /** Active template type — placeholders whose key isn't allowed for this type are dropped. */
  templateType?: TemplateType;
  /** Active template subtypes, forwarded into the resolver context. */
  templateSubtypes?: readonly string[];
}

export async function substitutePlaceholders(
  editor: FabricEditor,
  ctx: ResolverContext,
  options: SubstitutePlaceholdersOptions = {},
): Promise<void> {
  const facade = editor.placeholders;
  if (!facade) return;
  const effectiveCtx: ResolverContext = options.templateSubtypes
    ? { ...ctx, templateSubtypes: options.templateSubtypes }
    : ctx;
  const targets = editor.canvas.getObjects().filter((o): o is fabric.Group => facade.isPlaceholder(o));
  const tasks: Promise<void>[] = [];
  for (const group of targets) {
    const data = getPlaceholderData(group);
    if (!data) continue;
    // Why: a placeholder whose key isn't valid for the active template type
    // never reaches the resolver. It's dropped from the export the same way
    // a placeholder with no resolved value is — keeps the rendered image clean
    // even though the editor shows it in red so authors can find and fix it.
    if (options.templateType && !isPlaceholderKeyValidFor(options.templateType, data.placeholderKey)) {
      editor.canvas.remove(group);
      continue;
    }
    const value = resolvePlaceholder(data, effectiveCtx);
    if (!value) {
      editor.canvas.remove(group);
      continue;
    }
    tasks.push(applyWithFallback(editor, facade, group, value));
  }
  await Promise.all(tasks);
  editor.canvas.requestRenderAll();
}

/**
 * Applies a resolved value, retrying once with the value's `fallbackUrl` when
 * the image fails to decode (network error, non-image response, zero-dimension
 * result). `applyResolved` reports that as `false` and leaves the group
 * untouched on the canvas, so the retry can target the same group.
 *
 * A placeholder that exhausts its URLs is dropped, the same way one with no
 * resolved value is — a broken image URL must not leave the placeholder cue in
 * the exported PNG.
 */
async function applyWithFallback(
  editor: FabricEditor,
  facade: PlaceholderFacade,
  group: fabric.Group,
  value: ResolvedValue,
): Promise<void> {
  if (await facade.applyResolved(group, value)) return;
  const fallbackUrl = value.type === 'image' ? value.fallbackUrl : undefined;
  if (fallbackUrl && (await facade.applyResolved(group, { type: 'image', url: fallbackUrl }))) return;
  editor.canvas.remove(group);
}
